import type { Dispatch } from 'redux';
import { Form, FormRenderProps } from 'react-final-form';
import equal from 'fast-deep-equal';
import { connect, useDispatch } from 'react-redux';
import { useCallback } from 'react';

import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { ISimilarityStatusFilterEnum } from 'APIPackage/SimilarityModule/interfaces/ISimilarityStatus';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { ISimilaritiesMatchByPersonFilter } from 'APIPackage/SimilarityModule/interfaces/ISimilaritiesMatchByPersonFilter';
import { DATE_FORMAT_ISO_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { nextYear } from 'SimilaritiesPackage/MainModule/constants/nextYear';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { getAccountProsecutorOrganizationsSelectOptions } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  similaritiesGetDataById,
  similaritiesSetFiltersById,
} from 'SimilaritiesPackage/MainModule/actions/similaritiesActions';
import { getSimilaritiesPersonId } from 'SimilaritiesPackage/MainModule/selectors/similaritiesSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';

import styles from './SimilaritiesByPersonFiltersModal.module.scss';

const emptySelect = { id: '', label: '', value: '' };

const similarityStatusesOptions = [
  {
    id: ISimilarityStatusFilterEnum.UNRESOLVED,
    label: 'Неразрешенное',
    value: ISimilarityStatusFilterEnum.UNRESOLVED,
  },
  {
    id: ISimilarityStatusFilterEnum.JOINTLY,
    label: 'Совместное',
    value: ISimilarityStatusFilterEnum.JOINTLY,
  },
  {
    id: ISimilarityStatusFilterEnum.RESOLVED,
    label: 'Принудительно разрешенное',
    value: ISimilarityStatusFilterEnum.RESOLVED,
  },
];

const yearOption = [
  {
    id: nextYear,
    label: nextYear,
    value: nextYear,
  },
];

interface ISimilaritiesByPersonFiltersModalProps {
  isVisible?: boolean;
  onClose: () => void;
}

interface ISimilaritiesByPersonFiltersModalStoreProps {
  personId: string | undefined;
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[];
}

interface IFiltersModalForm {
  year: ISelectOption;
  prosecutorId: IUserOrganisationSelectOption;
  isIncludeChildProsec: boolean;
  matchStatus: ISelectOption[];
  daysOrHours: ISelectOption;
  daysOrHoursQuantity: number;
  fromStartDate: string;
  toStartDate: string;
}

const SimilaritiesByPersonFiltersModalWithoutState = ({
  isVisible,
  onClose,
  personId,
  userProsecutorOrganisationsSelectOptions,
}: ISimilaritiesByPersonFiltersModalProps & ISimilaritiesByPersonFiltersModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const prosecutorByDefault = userProsecutorOrganisationsSelectOptions.filter(
    (org: { byDefault?: boolean }) => org.byDefault,
  )[0];

  const onSubmit = useCallback(
    (data: IFiltersModalForm) => {
      const {
        year,
        prosecutorId,
        isIncludeChildProsec,
        matchStatus,
        fromStartDate,
        toStartDate,
        daysOrHours,
        daysOrHoursQuantity,
      } = data;
      const filtersMap: ISimilaritiesMatchByPersonFilter = {
        year: Number(year?.id) === 0 ? Number(nextYear) : Number(year?.id),
        prosecutorId: Number(prosecutorId.id),
        includeChildProsec: isIncludeChildProsec,
        matchStatuses: matchStatus?.map(status => status.label),
        ...(daysOrHours?.id === 'days' && { durationDays: Number(daysOrHoursQuantity) }),
        ...(daysOrHours?.id === 'hours' && { durationHours: Number(daysOrHoursQuantity) }),
        ...(fromStartDate !== '' && {
          fromStartDate: formatDate({ date: fromStartDate, dateFormat: DATE_FORMAT_ISO_FNS }),
        }),
        ...(toStartDate !== '' && {
          toStartDate: formatDate({ date: toStartDate, dateFormat: DATE_FORMAT_ISO_FNS }),
        }),
        id: personId,
      };

      dispatch(similaritiesSetFiltersById(filtersMap));
      dispatch(similaritiesGetDataById());
      onClose();
    },
    [dispatch, onClose, personId],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<IFiltersModalForm>) => (
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Фильтры" color="black" />
        <ModalBody size="content" border="bottom">
          <Row>
            <FieldBlock label="Год сводного плана" color="black">
              <SelectInputField name="year" options={yearOption} />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Прокуратура" color="black">
              <SelectInputField name="prosecutorId" options={userProsecutorOrganisationsSelectOptions} disabled />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock>
              <CheckboxField
                id="isIncludeChildProsec"
                theme="system"
                size="medium"
                name="isIncludeChildProsec"
                label="С учетом подчиненных прокуратур"
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Статус КНМ" color="black">
              <SelectInputField name="matchStatus" options={similarityStatusesOptions} isMulti />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Длительность проверки" color="black">
              <Row notWrap notSpace>
                <FieldBlock margin="right">
                  <SelectInputField
                    name="daysOrHours"
                    options={[
                      { id: 'days', label: 'В днях', value: 'days' },
                      { id: 'hours', label: 'В часах', value: 'hours' },
                    ]}
                    isMulti
                  />
                </FieldBlock>
                <FieldBlock>
                  <TextInputField name="daysOrHoursQuantity" />
                </FieldBlock>
              </Row>
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Дата начала проведения КНМ (интервал)" color="black">
              <Row notWrap notSpace>
                <span className={styles.FilterFormGroupDatePickersLabel}>С</span>
                <FieldBlock margin="right">
                  <DatePickerField name="fromStartDate" opensOnTop isEqual={equal} />
                </FieldBlock>
                <span className={styles.FilterFormGroupDatePickersLabelNotFirst}>по</span>
                <FieldBlock>
                  <DatePickerField name="toStartDate" opensOnTop isEqual={equal} />
                </FieldBlock>
              </Row>
            </FieldBlock>
          </Row>
        </ModalBody>
        <ModalActions>
          <Button type="submit" color="primary" size="medium" text="Применить" />
          <Button type="button" onClick={onClose} color="default" size="medium" text="Отменить" />
        </ModalActions>
      </form>
    ),
    [onClose, userProsecutorOrganisationsSelectOptions],
  );

  return (
    <Modal modalBodyClassName={styles.Modal} visible={isVisible} onClose={onClose}>
      <Form<IFiltersModalForm>
        keepDirtyOnReinitialize
        initialValues={{
          year: emptySelect,
          prosecutorId: prosecutorByDefault,
          isIncludeChildProsec: false,
          matchStatus: [],
          daysOrHours: emptySelect,
          daysOrHoursQuantity: 0,
          fromStartDate: '',
          toStartDate: '',
        }}
        onSubmit={onSubmit}
        render={renderForm}
      />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): ISimilaritiesByPersonFiltersModalStoreProps => ({
  personId: getSimilaritiesPersonId(state),
  userProsecutorOrganisationsSelectOptions: getAccountProsecutorOrganizationsSelectOptions(state),
});

export const SimilaritiesByPersonFiltersModal = connect(mapStateToProps)(SimilaritiesByPersonFiltersModalWithoutState);
